/**
 * GENERATED FILE — do not edit by hand.
 * Regenerate with:  npm run fit:curves
 *
 * Fitted positional stat curves (§4.5). Each entry models one stat's expected
 * season total as a function of positional rank, as a Hill function:
 *
 *     value(rank) = L / (1 + (rank / r0)^k)
 *
 * L is the shoulder height at the top of the position, r0 is where the curve
 * has fallen by half (the positional cliff), and k is how sharp that cliff is.
 *
 * Source: nflverse regular-season player and team totals, seasons 2021, 2022, 2023, 2024, 2025
 * (openly licensed). Fitted 2026-07-27 by scripts/fit-curves.ts.
 *
 * `r2` is the coefficient of determination of the fit in raw units, kept so the
 * UI can label a weak curve honestly rather than presenting every number with
 * the same confidence.
 */

export interface StatCurve {
  /** Asymptotic value as rank approaches 0 — the top-of-position shoulder. */
  L: number;
  /** Rank at which the stat has fallen to half of L — the positional cliff. */
  r0: number;
  /** Sharpness of the cliff. */
  k: number;
  /** Coefficient of determination of the fit, in raw units. */
  r2: number;
}

export interface PositionCurve {
  /** Ranks beyond this were not fitted; evaluation clamps to this depth. */
  depth: number;
  /** Sleeper scoring_settings key -> fitted curve. */
  stats: Record<string, StatCurve>;
}

export const CURVE_SEASONS: readonly number[] = [2021, 2022, 2023, 2024, 2025];

export const POSITION_CURVES: Record<string, PositionCurve> = {
  QB: {
    depth: 40,
    stats: {
      "fum_lost": { L: 3.0436, r0: 35.1667, k: 5.15, r2: 0.9484 },
      "pass_2pt": { L: 1.1843, r0: 32.8333, k: 5.85, r2: 0.887 },
      "pass_att": { L: 555.3965, r0: 30.5, k: 3.25, r2: 0.9868 },
      "pass_cmp": { L: 370.1006, r0: 29.5, k: 3.15, r2: 0.9864 },
      "pass_int": { L: 10.4815, r0: 35.5, k: 5.2, r2: 0.9497 },
      "pass_td": { L: 33.3733, r0: 20.5, k: 2.05, r2: 0.9797 },
      "pass_yd": { L: 4213.8946, r0: 28.1667, k: 2.95, r2: 0.9884 },
      "rec": { L: 0.2225, r0: 22.1, k: 3.1, r2: 0.9521 },
      "rec_2pt": { L: 0.2152, r0: 2.5, k: 6, r2: 0.9149 },
      "rec_td": { L: 0.1219, r0: 7.3, k: 6, r2: 0.9112 },
      "rec_tgt": { L: 0.2845, r0: 23.625, k: 1.8, r2: 0.9163 },
      "rec_yd": { L: 1.4991, r0: 8.5, k: 2.3, r2: 0.8682 },
      "rush_2pt": { L: 1.4401, r0: 0.5, k: 0.6, r2: 0.8964 },
      "rush_att": { L: 109.6815, r0: 13.1667, k: 1.05, r2: 0.9408 },
      "rush_td": { L: 16.8779, r0: 1.1667, k: 0.75, r2: 0.941 },
      "rush_yd": { L: 714.9217, r0: 5.8333, k: 0.85, r2: 0.9439 },
    },
  },
  RB: {
    depth: 80,
    stats: {
      "fum_lost": { L: 1.3386, r0: 39.8333, k: 2.6, r2: 0.9773 },
      "pass_att": { L: 0.2572, r0: 16.9583, k: 2.25, r2: 0.9589 },
      "pass_cmp": { L: 0.1606, r0: 13.3333, k: 2.05, r2: 0.9414 },
      "pass_int": { L: 0.0207, r0: 22.8833, k: 2.85, r2: 0.8163 },
      "pass_td": { L: 0.104, r0: 15.625, k: 2.2, r2: 0.9488 },
      "pass_yd": { L: 7.5431, r0: 0.9583, k: 1.2, r2: 0.8732 },
      "rec": { L: 56.384, r0: 33.1667, k: 1.4, r2: 0.958 },
      "rec_2pt": { L: 0.1048, r0: 30.3083, k: 2.45, r2: 0.9314 },
      "rec_td": { L: 4.7007, r0: 8.5, k: 1, r2: 0.9357 },
      "rec_tgt": { L: 69.9027, r0: 35.1667, k: 1.45, r2: 0.9635 },
      "rec_yd": { L: 505.6022, r0: 22.5, k: 1.1, r2: 0.9429 },
      "rush_2pt": { L: 0.5252, r0: 16.125, k: 1.6, r2: 0.9523 },
      "rush_att": { L: 261.1376, r0: 37.1667, k: 2.2, r2: 0.9864 },
      "rush_td": { L: 13.1788, r0: 17.8333, k: 1.4, r2: 0.975 },
      "rush_yd": { L: 1256.7566, r0: 31.8333, k: 1.9, r2: 0.9797 },
      "st_td": { L: 0.021333, r0: 1000000000, k: 1, r2: 0 },
    },
  },
  WR: {
    depth: 100,
    stats: {
      "fum_lost": { L: 0.5683, r0: 69.6667, k: 2.15, r2: 0.8143 },
      "pass_2pt": { L: 0.0191, r0: 20.3333, k: 0.6, r2: 0.7027 },
      "pass_att": { L: 0.7875, r0: 4.625, k: 0.8, r2: 0.8762 },
      "pass_cmp": { L: 0.7788, r0: 1.2917, k: 0.75, r2: 0.8198 },
      "pass_int": { L: 0.0537, r0: 9.05, k: 0.9, r2: 0.7179 },
      "pass_td": { L: 0.2072, r0: 2.275, k: 0.95, r2: 0.8345 },
      "pass_yd": { L: 14.7295, r0: 1.2917, k: 0.75, r2: 0.8334 },
      "rec": { L: 111.5354, r0: 39.6667, k: 1.2, r2: 0.9758 },
      "rec_2pt": { L: 0.3558, r0: 55.5, k: 2.75, r2: 0.8892 },
      "rec_td": { L: 11.5441, r0: 23.8333, k: 1.1, r2: 0.9695 },
      "rec_tgt": { L: 158.461, r0: 48, k: 1.3, r2: 0.9849 },
      "rec_yd": { L: 1572.6671, r0: 32.1667, k: 1.1, r2: 0.975 },
      "rush_att": { L: 20.4073, r0: 0.5, k: 0.4, r2: 0.7106 },
      "rush_td": { L: 2.7019, r0: 0.5, k: 0.7, r2: 0.7186 },
      "rush_yd": { L: 117.6996, r0: 0.5, k: 0.4, r2: 0.6795 },
      "st_td": { L: 0.0534, r0: 110.5, k: 6, r2: 0.7097 },
    },
  },
  TE: {
    depth: 40,
    stats: {
      "fum_lost": { L: 0.5226, r0: 21.1667, k: 1.55, r2: 0.8613 },
      "pass_att": { L: 2.4974, r0: 14.3, k: 6, r2: 0.9098 },
      "pass_cmp": { L: 1.4596, r0: 14.25, k: 6, r2: 0.8358 },
      "pass_int": { L: 0.0737, r0: 16.5, k: 5.6, r2: 0.8429 },
      "pass_td": { L: 0.1, r0: 1000000000, k: 1, r2: 0 },
      "pass_yd": { L: 19.7373, r0: 14, k: 6, r2: 0.8338 },
      "rec": { L: 121.327, r0: 9.5, k: 0.75, r2: 0.9651 },
      "rec_2pt": { L: 1.9152, r0: 0.5, k: 0.6, r2: 0.7838 },
      "rec_td": { L: 8.7786, r0: 11.8333, k: 1.05, r2: 0.9536 },
      "rec_tgt": { L: 163.9552, r0: 10.5, k: 0.75, r2: 0.9641 },
      "rec_yd": { L: 1327.029, r0: 9.1667, k: 0.8, r2: 0.9731 },
      "rush_att": { L: 4.939, r0: 15.9167, k: 3.9, r2: 0.949 },
      "rush_td": { L: 0.3902, r0: 13.7583, k: 2.15, r2: 0.9016 },
      "rush_yd": { L: 24.3944, r0: 14.9917, k: 3.05, r2: 0.9424 },
    },
  },
  K: {
    depth: 36,
    stats: {
      "fgm_0_19": { L: 0.1189, r0: 16.5, k: 1.85, r2: 0.7803 },
      "fgm_20_29": { L: 7.5082, r0: 30.5, k: 5.9, r2: 0.916 },
      "fgm_30_39": { L: 10.0649, r0: 30.8, k: 2.4, r2: 0.8808 },
      "fgm_40_49": { L: 8.5298, r0: 29.9, k: 3.2, r2: 0.9415 },
      "fgm_50_59": { L: 8.1935, r0: 17, k: 1.5, r2: 0.9005 },
      "fgm_60p": { L: 0.7347, r0: 4, k: 1.05, r2: 0.8901 },
      "fgmiss": { L: 4.0285, r0: 37.1, k: 6, r2: 0.7641 },
      "xpm": { L: 38.5357, r0: 30.5, k: 5.4, r2: 0.946 },
      "xpmiss": { L: 1.3812, r0: 36.2, k: 6, r2: 0.6881 },
    },
  },
  DEF: {
    depth: 32,
    stats: {
      "def_st_td": { L: 3.3584, r0: 0.5, k: 0.5, r2: 0.8417 },
      "def_td": { L: 7.4638, r0: 0.7667, k: 0.65, r2: 0.8709 },
      "fum_rec": { L: 10.7926, r0: 38.6333, k: 1.6, r2: 0.968 },
      "int": { L: 18.7219, r0: 26.9, k: 1.4, r2: 0.9678 },
      "sack": { L: 48.5904, r0: 48.2333, k: 1.55, r2: 0.8958 },
      "safe": { L: 1.0965, r0: 1.7917, k: 0.25, r2: 0.7546 },
    },
  },
};
